import "@/styles/index.css";

import { WButton } from "@/components/button/WButton.tsx";
import { PageLoadingSuspense } from "@/components/loading/Loading.tsx";
import { configFetchedAtom, configWaitFetchAtom } from "@/config.fetch.ts";
import { mockConfigAtom } from "@/config.mock.ts";
import { injectedConfigAtom } from "@/config.ts";
import * as debugConfig from "@/debugConfig.ts";
import { addDevVars } from "@/dev/initDev.ts";
import { isMockApiEnabled } from "@/mock-api/msw/mswInit.ts";
import { ApiCredentialsError } from "@/modules/api/ApiError.ts";
import { useStoreReferralCode } from "@/modules/billing/referral/referralLogic.ts";
import { ErrorBox } from "@/modules/error/ErrorBox.tsx";
import { ErrorToaster } from "@/modules/error/ErrorToaster.tsx";
import { ReportingErrorBoundary } from "@/modules/error/ReportingErrorBoundary.tsx";
import { LoadLanguage, ProvideLanguage } from "@/modules/l10n/i18n.tsx";
import { AppRouterProvider } from "@/modules/main/AppRouterProvider.tsx";
import { ColorSchemeSwitcher } from "@/modules/main/ColorSchemeSwitcher.tsx";
import { errorToReactMessage } from "@/modules/notifications/errorToReactMessage.tsx";
import { initTheme } from "@/modules/theme/init.ts";
import { LogoutButton } from "@/modules/user/LogoutButton.tsx";
import { useLoadThemeScripts } from "@/ScriptLoader.tsx";
import { getEnvironment } from "@/utils/environment.ts";
import { Trans } from "@lingui/react/macro";
import { Provider, useAtomValue } from "jotai/react";
import { useHydrateAtoms } from "jotai/utils";
import { getDefaultStore } from "jotai/vanilla";
import { type ReactNode, StrictMode } from "react";

/**
 * Topmost component. Creates context and providers used by *all* components across all pages.
 *
 * Contexts and providers that depend on the router are initialized inside {@link AppRouterProvider} instead.
 */
export default function Root() {
    // TODO: <LoadingSuspense> will be rendered without theme styles when config loading takes too long
    let rootNode = (
        <Provider store={getDefaultStore()}>
            {/* NB: We must load i18n before anything since eg. even error messages can depend on it */}
            <ProvideLanguage>
                <MainErrorBoundary>
                    {getEnvironment() !== "production" && <ErrorToaster />}
                    <ColorSchemeSwitcher />
                    {/* Inner suspense for config loading */}
                    <PageLoadingSuspense>
                        <RootInitializer />
                    </PageLoadingSuspense>
                </MainErrorBoundary>
            </ProvideLanguage>
        </Provider>
    );
    if (import.meta.env.DEV && debugConfig.reactStrictMode) {
        rootNode = <StrictMode>{rootNode}</StrictMode>;
    }
    return rootNode;
}

/** Calls initialization hooks before mounting the router. Separated so the hooks could use contexts provided by {@link Root} and/or suspend. */
function RootInitializer() {
    useLoadConfig();
    // NB: Initialize theme before rendering any components but after loading config (since it uses `siteTheme`)
    initTheme();
    useStoreReferralCode();
    useLoadThemeScripts();

    return (
        // XXX: Language loading also triggers a suspense but the loader is not rendered properly because the theme is not loaded yet
        <LoadLanguage>
            <AppRouterProvider />
        </LoadLanguage>
    );
}

/**
 * Pre-load configuration.
 * NB: Must run before any components are rendered.
 */
function useLoadConfig() {
    const isMockApi = !import.meta.env.PROD && isMockApiEnabled();

    // Makes sure config has been fetched before proceeding (this will suspend until it is)
    useAtomValue(isMockApi ? mockConfigAtom : configWaitFetchAtom);
    useHydrateAtoms([[injectedConfigAtom, isMockApi ? mockConfigAtom : configFetchedAtom]]);
}

//#region Error handling

export function MainErrorMessage({ error }: { error: unknown }) {
    const isAuthError = error instanceof ApiCredentialsError;

    return (
        <ErrorBox>
            <div className="text-left mb-2">{errorToReactMessage(error)}</div>
            <div className="HStack mt-2 gap-2">
                {!isAuthError && (
                    <WButton action={() => location.reload()} color="primary">
                        <Trans>Reload page</Trans>
                    </WButton>
                )}
                <LogoutButton />
            </div>
        </ErrorBox>
    );
}

function MainErrorBoundary({ children }: { children: ReactNode }) {
    return (
        <ReportingErrorBoundary FallbackComponent={MainErrorMessage} tags="main">
            {children}
        </ReportingErrorBoundary>
    );
}

//#endregion

addDevVars({ store: getDefaultStore() });
